/**
 * SchoolBackupSchedulePanel — weekly cron + email recipient list config
 * for the School Backup Centre. Used inside AdminBackupCentre.
 *
 * Backend wiring
 *   GET  /api/school/backup/schedule?tenant_id=
 *   PUT  /api/school/backup/schedule
 *   POST /api/school/backup/schedule/run-now?tenant_id=  → returns a ZIP
 *   GET  /api/school/backup/schedule/runs?tenant_id=     → audit log
 *
 * Cron expressions are validated server-side; common presets are exposed
 * as one-click buttons so operators don't have to learn cron syntax.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  CalendarClock, Mail, Save, Play, X, History, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' });

const PRESETS = [
  { label: 'Sundays 02:00 UTC', value: '0 2 * * 0' },
  { label: 'Every Friday 23:00 UTC', value: '0 23 * * 5' },
  { label: 'Every day 03:00 UTC', value: '0 3 * * *' },
  { label: '1st of month 02:00 UTC', value: '0 2 1 * *' },
];

const STATUS_BADGE = {
  ok: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  email_failed: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  failed: 'bg-rose-500/15 text-rose-300 border-rose-500/40',
  running: 'bg-sky-500/15 text-sky-300 border-sky-500/40',
  no_recipients: 'bg-slate-500/15 text-slate-300 border-slate-500/40',
};

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export const SchoolBackupSchedulePanel = ({ tenantId }) => {
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState(false);
  const [cron, setCron] = useState('0 2 * * 0');
  const [emails, setEmails] = useState('');

  const qs = tenantId ? `?tenant_id=${tenantId}` : '';

  const { data: sched, refetch: refetchSched } = useQuery({
    queryKey: ['backup-schedule', tenantId],
    queryFn: async () => (await axios.get(`${API}/api/school/backup/schedule${qs}`, { headers: authH() })).data,
  });

  const { data: runs = [], refetch: refetchRuns } = useQuery({
    queryKey: ['backup-runs', tenantId],
    queryFn: async () => (await axios.get(`${API}/api/school/backup/schedule/runs${qs}&limit=10`.replace('?&', '?'), { headers: authH() })).data,
    enabled: !!sched?.exists,
  });

  useEffect(() => {
    if (sched) {
      setEnabled(!!sched.enabled);
      setCron(sched.cron_expression || '0 2 * * 0');
      setEmails((sched.email_to || []).join(', '));
    }
  }, [sched]);

  const save = useMutation({
    mutationFn: async () => {
      const emailList = emails.split(',').map(s => s.trim()).filter(Boolean);
      return (await axios.put(`${API}/api/school/backup/schedule`,
        { tenant_id: tenantId || null, enabled, cron_expression: cron, email_to: emailList },
        { headers: authH() })).data;
    },
    onSuccess: () => {
      toast.success('Schedule saved');
      qc.invalidateQueries({ queryKey: ['backup-schedule'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Save failed'),
  });

  const runNow = useMutation({
    mutationFn: async () => {
      if (!tenantId) {
        toast.error('Set a tenant ID first (the ad-hoc download needs one).');
        throw new Error('tenant_id required');
      }
      const res = await axios.post(
        `${API}/api/school/backup/schedule/run-now?tenant_id=${tenantId}`,
        null,
        { headers: authH(), responseType: 'blob' });
      const counts = res.headers['x-row-counts'] || '';
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `school-backup.zip`;
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a'); a.href = blobUrl; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      return counts;
    },
    onSuccess: (counts) => { toast.success(`Backup ZIP downloaded · ${counts}`); refetchRuns(); },
    onError: (e) => { if (!e.message?.includes('tenant_id')) toast.error('Backup run failed'); },
  });

  return (
    <Card className="bg-slate-900/40 border-slate-800/60" data-testid="backup-schedule-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base text-white flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-indigo-400" /> Scheduled email backups
        </CardTitle>
        <p className="text-xs text-slate-400 mt-1">
          Auto-bundles all four CSVs (exam marks · attendance · library · fees) into a single
          ZIP and emails it on the schedule below. Server is the source of truth — re-saves
          re-register the APScheduler job immediately.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-slate-900/60 border border-slate-800 rounded px-3 py-2">
              <div>
                <Label className="text-sm text-slate-200">Enabled</Label>
                <p className="text-[11px] text-slate-500">Disabling stops future runs but keeps the configured cron + recipients.</p>
              </div>
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="backup-schedule-enabled" />
            </div>

            <div>
              <Label className="text-xs text-slate-400">Cron expression (UTC, 5 fields)</Label>
              <Input value={cron} onChange={(e) => setCron(e.target.value)}
                      className="bg-slate-900/60 border-slate-700 mt-1 font-mono"
                      data-testid="backup-schedule-cron" placeholder="0 2 * * 0" />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {PRESETS.map(p => (
                  <Button key={p.value} size="sm" variant="outline"
                          className="h-7 text-xs border-slate-700 text-slate-300"
                          onClick={() => setCron(p.value)}>
                    {p.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-400">Recipients (comma-separated emails)</Label>
              <Input value={emails} onChange={(e) => setEmails(e.target.value)}
                      className="bg-slate-900/60 border-slate-700 mt-1"
                      data-testid="backup-schedule-emails"
                      placeholder="principal@school.edu, finance@school.edu" />
              <p className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
                <Mail className="h-3 w-3" /> First successful schedule run will send the ZIP attachment.
              </p>
            </div>

            <div className="bg-slate-900/60 border border-slate-800 rounded p-3 text-xs space-y-1">
              <div className="text-slate-400">Last run</div>
              <div className="flex items-center gap-2">
                {sched?.last_status
                  ? <Badge className={`${STATUS_BADGE[sched.last_status] || 'bg-slate-500/15 text-slate-300 border-slate-500/40'} border`}>{sched.last_status}</Badge>
                  : <Badge variant="outline" className="text-slate-500">never run</Badge>}
                <span className="text-slate-500">{fmt(sched?.last_run_at)}</span>
              </div>
              {sched?.last_row_counts && (
                <div className="text-slate-500 text-[11px]">
                  Rows: {Object.entries(sched.last_row_counts).map(([k, v]) => `${k}=${v}`).join(' · ')}
                  {sched.last_zip_size ? ` · ${Math.round(sched.last_zip_size / 1024)} KB` : ''}
                </div>
              )}
              {sched?.last_error && <div className="text-rose-300 text-[11px]">⚠ {sched.last_error}</div>}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-slate-800">
          <Button variant="outline" size="sm" onClick={() => runNow.mutate()} disabled={runNow.isPending}
                  className="border-slate-700 text-slate-200"
                  data-testid="backup-run-now">
            <Play className="h-4 w-4 mr-1" /> {runNow.isPending ? 'Building…' : 'Download backup now'}
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="backup-schedule-save">
            <Save className="h-4 w-4 mr-1" /> {save.isPending ? 'Saving…' : 'Save schedule'}
          </Button>
        </div>

        {runs.length > 0 && (
          <details className="pt-2">
            <summary className="text-xs text-slate-400 cursor-pointer inline-flex items-center gap-1">
              <History className="h-3.5 w-3.5" /> Recent runs ({runs.length})
            </summary>
            <div className="mt-2 space-y-1.5 max-h-60 overflow-y-auto">
              {runs.map((r) => (
                <div key={r._id} className="flex items-center justify-between bg-slate-900/40 border border-slate-800/60 rounded px-3 py-1.5 text-xs">
                  <div className="flex items-center gap-2">
                    {r.status === 'ok'
                      ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" />
                      : <AlertCircle className="h-3.5 w-3.5 text-amber-300" />}
                    <span className="text-slate-300">{fmt(r.started_at)}</span>
                    <Badge className={`${STATUS_BADGE[r.status] || ''} border text-[10px]`}>{r.status}</Badge>
                  </div>
                  <div className="text-slate-500">
                    {r.zip_bytes ? `${Math.round(r.zip_bytes / 1024)} KB · ` : ''}
                    {r.recipients?.length || 0} recipient{(r.recipients?.length || 0) === 1 ? '' : 's'}
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
};

export default SchoolBackupSchedulePanel;
